'use client';

import { Currency } from '@/api/types/public';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

/** PayPopData 接口，可根据后端返回拓展 */
interface CouponStairs {
  discounts_value: number | string;
  threshold: number | string;
}

interface DiscountsDetails {
  discounts_type?: number; // 1普通折扣 2阶梯折扣
  discounts_value?: number | string;
  discounts_threshold?: number | string;
  discounts_threshold_type?: number; // 1金额 2件数
  discounts_stairs_type?: number; // 1金额 2件数
  discounts_stairs?: CouponStairs[];
}

interface CouponInfo {
  id?: number | string;
  way?: number; // 1,2,3,4,5
  discounts_details?: DiscountsDetails;
  full_discounts_details?: {
    discounts_value?: number | string;
    discounts_threshold?: number | string;
  };
  appoint_discounts_details?: {
    discounts_value?: number | string;
  };
  free_discounts_details?: {
    discounts_type?: number;
    number?: number;
  };
}

interface PayPopData {
  btn_text?: string;
  btn_color?: string;
  title_color?: string;
  title?: string; // html string
  coupon_discount_price?: number | string;
  coupon?: CouponInfo;
}

export interface RetainPopupProps {
  open: boolean;
  discountPrice?: number | string;
  type?: boolean; // true 有优惠券，false 无优惠券
  payPopData: PayPopData;
  onApplyCode: (coupon: CouponInfo) => void;
  onClose: () => void;
  onApplyRetainCoupon: () => void;
  onReturnToCart: () => void;
}

/** 生成 couponTitle */
const genCouponTitle = (
  coupon?: CouponInfo,
  currency?: Currency,
  globalT?: (key: string, values?: any) => string,
) => {
  if (!coupon) return '';
  const { way, discounts_details: d } = coupon;
  switch (way) {
    case 1:
    case 5:
      if (!d) return '';
      return d.discounts_type === 1
        ? `${d.discounts_value}% ${globalT('coupons.off')}`
        : `${d.discounts_stairs?.[0]?.discounts_value}% ${globalT('coupons.off')}`;
    case 2:
      return `${formatPriceNumberWithSymbol(`${coupon.full_discounts_details?.discounts_value}`, currency)} ${globalT('coupons.off')}`;
    case 3:
      return 'SALE';
    case 4:
      return globalT('coupons.free');
    default:
      return '';
  }
};

/** 生成 couponDesc */
const genCouponDesc = (
  coupon?: CouponInfo,
  currency?: any,
  ifSiteWide = false,
  globalT?: (key: string, values?: any) => string,
): string => {
  if (!coupon) return '';
  const {
    way,
    discounts_details: d,
    full_discounts_details: f,
    appoint_discounts_details: a,
    free_discounts_details: fr,
  } = coupon;
  switch (way) {
    case 1:
    case 5: {
      if (!d) return '';
      if (d.discounts_type === 1) {
        const threshold = d.discounts_threshold;
        if (d.discounts_threshold_type === 1) {
          return Number(threshold) === 0.01
            ? globalT('coupons.sitewideSale')
            : globalT('coupons.forOrdersOver', {
                price: formatPriceNumberWithSymbol(`${threshold}`, currency),
              });
        }
        return Number(threshold) === 1
          ? globalT('coupons.sitewideSale')
          : `${threshold} ${globalT('coupons.items')}&${globalT('coupons.above')}`;
      }
      // 阶梯折扣
      const stairType = d.discounts_stairs_type;
      const stairThreshold = d.discounts_stairs?.[0]?.threshold;
      if (!stairThreshold) return '';
      if (stairType === 2) return `${stairThreshold} ${globalT('coupons.items')}+`;
      if (stairType === 1) return `${formatPriceNumberWithSymbol(`${stairThreshold}`, currency)} +`;
      return '';
    }
    case 2:
      return ifSiteWide
        ? globalT('coupons.sitewideSale')
        : globalT('coupons.forOrdersOver', {
            price: formatPriceNumberWithSymbol(`${f?.discounts_threshold}`, currency),
          });
    case 3:
      return `PRICE ${formatPriceNumberWithSymbol(`${a?.discounts_value}`, currency)}`;
    case 4: {
      const itemText = (fr?.number ?? 1) === 1 ? globalT('coupons.item') : globalT('coupons.items');
      return fr?.discounts_type === 1
        ? `${globalT('components.coupon.highestPriced')} ${fr?.number} ${itemText}`
        : `${globalT('components.coupon.lowestPriced')} ${fr?.number} ${itemText}`;
    }
    default:
      return '';
  }
};

export default function RetainPopup({
  open,
  discountPrice = 0,
  type = false,
  payPopData,
  onApplyCode,
  onClose,
  onApplyRetainCoupon,
  onReturnToCart,
}: RetainPopupProps) {
  const globalT = useTranslations();
  const t = useTranslations('payment.Popup');
  const currency = useStore((s) => s.currency);

  const couponTitle = useMemo(
    () => genCouponTitle(payPopData?.coupon, currency, globalT),
    [payPopData, currency, globalT],
  );

  const ifSiteWide = useMemo(() => {
    const threshold = Number(payPopData?.coupon?.full_discounts_details?.discounts_threshold || 0);
    const value = Number(payPopData?.coupon?.full_discounts_details?.discounts_value || 0);
    return threshold * 100 - value * 100 === 1;
  }, [payPopData]);

  //  手工 mock data，测试翻译。就不删除吧，留着方便调试。
  // const couponDesc = useMemo(
  //   () =>
  //     genCouponDesc(
  //       {
  //         way: 4,
  //         discounts_details: {
  //           discounts_type: 1,
  //           discounts_value: 50,
  //           discounts_threshold: 1,
  //           discounts_threshold_type: 1,
  //         },
  //         free_discounts_details: {
  //           discounts_type: 1,
  //           number: 2,
  //         },
  //       },
  //       currency,
  //       ifSiteWide,
  //       globalT,
  //     ),
  //   [payPopData, currency, ifSiteWide],
  // );

  const couponDesc = useMemo(
    () => genCouponDesc(payPopData?.coupon, currency, ifSiteWide, globalT),
    [payPopData, currency, ifSiteWide, globalT],
  );

  const handleApplyCode = () => {
    if (payPopData?.coupon?.id) {
      onApplyCode(payPopData.coupon);
    }
    onClose();
  };

  const handleApplyCoupon = () => {
    onApplyRetainCoupon();
    onClose();
  };

  const handleReturn = () => {
    onReturnToCart();
    onClose();
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: payPopData.btn_color || undefined,
    color: payPopData.title_color || undefined,
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      hideHeader
      showCloseIcon={false}
      footer={null}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '320px', sm: '472px' },
          maxWidth: '100%',
          borderRadius: { xs: '16px', sm: '12px' },
          backgroundColor: 'transparent',
          padding: '0',
        },
        '& .MuiDialogContent-root': {
          padding: '0',
        },
      }}
    >
      <div className="flex flex-col items-center w-full bg-white rounded-[12px] overflow-hidden pb-[24px] relative">
        {/* Close icon handled by Dialog default closeIcon or we can add custom */}
        <div className="absolute top-16 right-16 cursor-pointer" onClick={onClose}>
          <CloseOutlined className="text-28" />
        </div>
        {/* 类型一：无优惠券 */}
        {!type && (
          <>
            <div className="flex flex-col items-center w-full bg-gradient-to-b from-[#BAACFF66] to-transparent pt-[32px] px-[32px]">
              {payPopData.coupon_discount_price && (
                <div className="py-[6px] px-[20px] bg-[#C7FF57] rounded-[4px] text-[16px] font-be text-primary-black notranslate">
                  {t('ExpectToSave')}{' '}
                  {formatPriceNumberWithSymbol(`${payPopData.coupon_discount_price}`, currency)}
                </div>
              )}
              <div className="my-[2px] font-be text-[56px] leading-[67px] text-primary-purple notranslate">
                {couponTitle}
              </div>
            </div>
            <div className="font-br text-[16px] text-primary-black leading-[19px] mt-[8px] notranslate">
              {couponDesc}
            </div>
            {/* tips html */}
            {payPopData.title && (
              <div
                className="mt-[20px] px-[32px] text-center text-[16px] leading-[24px] text-primary-black font-br mb-30"
                dangerouslySetInnerHTML={{ __html: payPopData.title }}
              />
            )}
            {/* 按钮 */}
            <Button
              variant="contained"
              className="w-[80%] md:w-[336px] h-[48px] font-bb mt-[32px]"
              style={buttonStyle}
              onClick={handleApplyCode}
              sx={{
                fontFamily: 'var(--font-bricolage-bold)',
              }}
            >
              {payPopData.btn_text}
            </Button>
          </>
        )}

        {/* 类型二：有优惠券 */}
        {type && (
          <>
            <div className="flex flex-col items-center w-full bg-gradient-to-b from-[#BAACFF66] to-transparent pt-[32px] px-[32px] price-wrap">
              <div className="text-[16px] leading-[19px] text-primary-black font-be">
                {t('PromotionSavings')}
              </div>
              <div className="font-be text-[56px] leading-[67px] text-primary-purple notranslate mt-[8px] text-center">
                {formatPriceNumberWithSymbol(`${discountPrice}`, currency)}
              </div>
            </div>
            {/* tips */}
            {payPopData.title && (
              <div
                className="mt-[20px] px-[32px] text-center text-[16px] leading-[24px] text-primary-black font-br mb-30"
                dangerouslySetInnerHTML={{ __html: payPopData.title }}
              />
            )}
            <Button
              variant="contained"
              className="w-[80%] md:w-[336px] h-[48px] font-be mt-[32px]"
              style={buttonStyle}
              onClick={handleApplyCoupon}
              sx={{
                fontFamily: 'var(--font-bricolage-bold)',
              }}
            >
              {payPopData.btn_text}
            </Button>
          </>
        )}

        {/* Return to Cart */}
        <div
          className="mt-[12px] text-[14px] underline cursor-pointer text-primary-black font-br"
          onClick={handleReturn}
        >
          {t('ReturnToCart')}
        </div>
      </div>
    </Dialog>
  );
}
